/**
 * Audit log — Cosmos `audit` container.
 *
 * Partition key: `/yearMonth` (e.g. "2026-05"). Bounded per-month queries.
 *
 * Append-only — no document is ever updated or deleted after creation.
 * GDPR right-to-erasure is satisfied by *redacting* the `actor` email +
 * name in-place (see Phase 7 endpoint `DELETE /api/users/:id/data`); the
 * audit row itself is preserved to maintain the compliance trail.
 *
 * The audit entry stores *summary* before/after snapshots, NOT full
 * documents — keep it to the changed fields only. (N6 removed the review
 * workflow; the `'review'` AuditSubjectType below is retained only so
 * historical audit rows still validate on read.)
 */

import { z } from 'zod';
import {
  CosmosBaseSchema,
  IsoTimestampSchema,
  UserRefSchema,
  YearMonthSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * Canonical action list. Anything not in this list cannot be written to the
 * audit log. New actions get added here + matching API path + Phase K+1 test.
 */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  // N6 (2026-06-09) — direct publish workflow (review step removed). An
  // authorized user publishes/unpublishes a flow directly. `publish-bundle`
  // is still emitted alongside `publish` to drive the public snapshot regen.
  'publish',
  'unpublish',
  /**
   * @deprecated N6 — review workflow removed. These three actions are no
   * longer emitted by any code path, but are kept in the enum so historical
   * audit rows written before the review removal still validate on read
   * (the audit log is append-only and must remain readable).
   */
  'submit-review',
  'approve-publish',
  'reject-review',
  'role-change',
  'login',
  'login-failed',
  'publish-bundle',
  'gdpr-erase',
  'archive',
  'unarchive',
  /**
   * @deprecated Legacy alias for `unarchive`; kept so audit rows persisted
   * before K+5 still validate. New code emits `unarchive`.
   */
  'restore',
  // Slice 2 pivot (M+5.7) — visibility on the public landing changed
  // (hidden | coming-soon | live). Independent of the publish state
  // machine; recorded so the audit log still tracks who flipped the
  // public surface and when.
  'visibility-changed',
  // Phase M+3.5 — submissions (replaces Power Automate pipeline)
  'submission-received',
  'submission-status-changed',
  'submission-reassigned',
  'submission-note-added',
  // M3 (customer-logo pipeline) — GDPR right-to-erasure for a submission.
  // Admin-only hard delete: the Cosmos doc is removed AND its logo blob is
  // erased from the PRIVATE container. The audit row PERSISTS (compliance
  // trail) even though the submission doc is gone.
  'submission-deleted',
  // F4 (GDPR access-trail) — a customer logo (PII) was downloaded by an
  // authenticated operator (any role) via GET /api/admin/submissions/:id/logo.
  // Persisted as a queryable audit row; like `submission-deleted` it records
  // only non-PII context (actor + submission id) — never the customer email
  // or logo bytes.
  'submission-logo-downloaded',
] as const;

export const AuditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof AuditActionSchema>;

// ---------------------------------------------------------------------------
// Subject of an audit entry (the thing acted upon)
// ---------------------------------------------------------------------------

export const AuditSubjectTypeSchema = z.enum([
  'flow',
  'landing',
  'user',
  // @deprecated N6 — the review workflow was removed; retained only so
  // historical audit rows with `subject.type === 'review'` still validate.
  'review',
  'system',
  // Phase M+3.5
  'submission',
]);
export type AuditSubjectType = z.infer<typeof AuditSubjectTypeSchema>;

export const AuditSubjectSchema = z
  .object({
    id: z.string().min(1).max(256),
    type: AuditSubjectTypeSchema,
  })
  .strict();
export type AuditSubject = z.infer<typeof AuditSubjectSchema>;

// ---------------------------------------------------------------------------
// Audit entry
// ---------------------------------------------------------------------------

/**
 * Free-form JSON snapshot of a small slice of the doc. Bounded to avoid
 * unbounded fields like full image binaries leaking into the log. Keep it
 * to the changed fields only.
 */
const SnapshotSchema = z.record(z.string(), z.unknown());

export const AuditEntrySchema = CosmosBaseSchema.extend({
  docType: z.literal('audit'),
  /** Partition key — must equal the YYYY-MM slice of `at`. */
  yearMonth: YearMonthSchema,
  actor: UserRefSchema,
  action: AuditActionSchema,
  subject: AuditSubjectSchema,
  /** ISO 8601 timestamp of when the action happened. */
  at: IsoTimestampSchema,
  /** Tracing id linking related events across services. */
  correlationId: z.string().min(1).max(128),
  /** Free-form human summary ("Spring Flow — 4 option price updates"). */
  what: z.string().min(1).max(1024),
  /** Optional small JSON snapshot of the doc before the change. */
  before: SnapshotSchema.optional(),
  /** Optional small JSON snapshot of the doc after the change. */
  after: SnapshotSchema.optional(),
  /** Optional structured diff (alternative to before/after for tabular UIs). */
  diff: z
    .array(
      z
        .object({
          field: z.string().min(1).max(256),
          before: z.string().max(2048),
          after: z.string().max(2048),
        })
        .strict(),
    )
    .optional(),
})
  .strict()
  .refine((v) => v.yearMonth === toYearMonth(v.at), {
    message: 'yearMonth must match the YYYY-MM of `at`',
    path: ['yearMonth'],
  });
export type AuditEntry = z.infer<typeof AuditEntrySchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive the YYYY-MM partition key from an ISO 8601 timestamp.
 * Throws on a malformed input — callers should only pass values they've
 * already validated via `IsoTimestampSchema`.
 */
export function toYearMonth(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    throw new Error(`toYearMonth: invalid ISO timestamp "${iso}"`);
  }
  const d = new Date(ts);
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0');
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  return `${yyyy}-${mm}`;
}
